import { io, Socket } from 'socket.io-client';
import { store } from './state';
import { SOCKET_URL, isNativePlatform } from './config';

let socket: Socket | null = null;
let appStateListener: { remove: () => void } | null = null;

type MessageHandler = (data: any) => void;
const messageHandlers: Map<string, Set<MessageHandler>> = new Map();

// Socket.IO reconnection configuration for mobile
const RECONNECTION_CONFIG = {
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 20000,
};

export function connect(): void {
  const state = store.getState();
  if (!state.accessToken) return;

  if (socket?.connected) return;

  socket = io(SOCKET_URL, {
    auth: {
      token: state.accessToken,
    },
    ...RECONNECTION_CONFIG,
  });

  socket.on('disconnect', (reason) => {
    // If server disconnected, attempt to reconnect
    if (reason === 'io server disconnect') {
      socket?.connect();
    }
  });

  // Set up app state listener for mobile lifecycle
  setupAppStateListener();

  // Forward events to registered handlers
  socket.onAny((event: string, data: any) => {
    const handlers = messageHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  });
}

export function disconnect(): void {
  // Clean up app state listener
  if (appStateListener) {
    appStateListener.remove();
    appStateListener = null;
  }

  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

// Set up listener for app state changes (foreground/background)
async function setupAppStateListener(): Promise<void> {
  if (!isNativePlatform()) return;

  try {
    const { App } = await import('@capacitor/app');

    // Remove existing listener if any
    if (appStateListener) {
      appStateListener.remove();
    }

    appStateListener = await App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        // App came to foreground - reconnect if needed
        if (socket && !socket.connected) {
          socket.connect();
        }
        // Re-register push token in case it rotated
        import('./push').then(m => m.refreshPushToken()).catch(() => {});
      }
    });
  } catch (err) {
    console.warn('Could not set up app state listener:', err);
  }
}

export function joinChat(chatId: string): void {
  socket?.emit('chat:join', chatId);
}

export function leaveChat(chatId: string): void {
  socket?.emit('chat:leave', chatId);
}

export function joinQaSet(setId: string): void {
  socket?.emit('qa:set:join', setId);
}

export function leaveQaSet(setId: string): void {
  socket?.emit('qa:set:leave', setId);
}

export function sendTyping(chatId: string, text: string): void {
  socket?.emit('message:typing', { chatId, text });
}

export function clearTyping(chatId: string): void {
  socket?.emit('message:typing:clear', { chatId });
}

export function on(event: string, handler: MessageHandler): void {
  if (!messageHandlers.has(event)) {
    messageHandlers.set(event, new Set());
  }
  messageHandlers.get(event)!.add(handler);
}

export function off(event: string, handler: MessageHandler): void {
  const handlers = messageHandlers.get(event);
  if (handlers) {
    handlers.delete(handler);
  }
}

// Convenience methods for common events
export function onNewMessage(handler: (data: { chatId: string; message: any }) => void): void {
  on('message:new', handler);
}

export function onMessageRead(handler: (data: { chatId: string; messageId: string; userId: string }) => void): void {
  on('message:read', handler);
}

export function onTyping(handler: (data: { chatId: string; userId: string; text: string }) => void): void {
  on('message:typing', handler);
}

export function onTypingClear(handler: (data: { chatId: string; userId: string }) => void): void {
  on('message:typing:clear', handler);
}

export function onNewMatch(handler: (data: { match: any }) => void): void {
  on('match:new', handler);
}

export function onUserOnline(handler: (data: { userId: string; online: boolean }) => void): void {
  on('user:online', handler);
}

export function onAiThinking(handler: (data: { chatId: string; aiUserId: string }) => void): void {
  on('ai:thinking', handler);
}

export function onAiUploading(handler: (data: { chatId: string; aiUserId: string }) => void): void {
  on('ai:uploading', handler);
}

export function onAiUploadingDone(handler: (data: { chatId: string }) => void): void {
  on('ai:uploading:done', handler);
}

export function onMessageApproved(handler: (data: { chatId: string; messageId: string; message: any }) => void): void {
  on('message:approved', handler);
}

export function onMessageRejected(handler: (data: { chatId: string; messageId: string }) => void): void {
  on('message:rejected', handler);
}

export function onChatCleared(handler: (data: { chatId: string }) => void): void {
  on('chat:cleared', handler);
}

export default {
  connect,
  disconnect,
  joinChat,
  leaveChat,
  sendTyping,
  clearTyping,
  on,
  off,
  onNewMessage,
  onMessageRead,
  onTyping,
  onTypingClear,
  onNewMatch,
  onUserOnline,
  onAiThinking,
  onAiUploading,
  onAiUploadingDone,
  onMessageApproved,
  onMessageRejected,
  onChatCleared,
};
